import isBefore from 'date-fns/isBefore';
import { calculateHoursBetweenDates, addZeroPrefix } from 'utils/dateAndTimeUtils';
import { format, addMinutes, subMinutes, subDays } from 'date-fns';
import { dirtyValuesFor } from 'utils';

export const createPayLoadDateTime = ({ time, originalDate }) => {
  const [timePart, meridiem] = time.split(' ');
  const [hours, minutes] = timePart.split(':').map((part) => parseInt(part, 10));

  // Adjust hours for PM and AM
  let adjustedHours = hours;
  if (meridiem === 'PM' && hours !== 12) {
    adjustedHours += 12;
  } else if (meridiem === 'AM' && hours === 12) {
    adjustedHours = 0;
  }

  return new Date(originalDate.getFullYear(), originalDate.getMonth(), originalDate.getDate(), adjustedHours, minutes);
};

export const isValidTimeFormat = (value) => {
  if (!value) return false;
  const regex = /^\d{2}:\d{2}:\d{2}$/;
  return regex.test(value);
};

const getMinutesFromTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  return (hours * 60) + minutes;
};

const calculateState = ({ startTime, endTime, selectedDate, startDateTimeOriginal, endDateTimeOriginal, isUpdatedEndDateTimeOriginal = false, changeEndValue, payloadStartDateTime, payloadEndDateTime }) => {
  let startValue = createPayLoadDateTime({ time: startTime, originalDate: changeEndValue ? (new Date(payloadStartDateTime) > new Date(selectedDate) ? selectedDate : payloadStartDateTime) : selectedDate });
  const endValue = createPayLoadDateTime({ time: endTime, originalDate: changeEndValue ? selectedDate : (new Date(payloadEndDateTime) < new Date(selectedDate) ? selectedDate : payloadEndDateTime) });

  let { total: totalHours, timeDifferenceInMinutes } = calculateHoursBetweenDates({ startDate: startValue, endDate: endValue, withSeconds: true });
  if (timeDifferenceInMinutes < 0) {
    startValue = subDays(startValue, 1);
    const { total } = calculateHoursBetweenDates({ startDate: startValue, endDate: endValue, withSeconds: true });
    totalHours = total;
  }

  const newValue = { startValue, endValue };
  const oldValue = { startValue: startDateTimeOriginal, endValue: endDateTimeOriginal };
  const isDirty = Object.keys(dirtyValuesFor(oldValue, newValue)).length > 0;
  return { isDirty, startTime, endTime, startValue, endValue, isUpdatedEndDateTimeOriginal, totalHours };
};

export default {
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.StartWrapper.StartDropdown': ({ startDateTimeOriginal, endDateTimeOriginal, selectedOptionId, selectedDate, endTime, payloadStartDateTime, payloadEndDateTime }, _, internalState) => {
    const { isDirty, startTime, totalHours, startValue, endValue } = calculateState({ startTime: selectedOptionId, endTime, selectedDate, startDateTimeOriginal, endDateTimeOriginal, payloadStartDateTime, payloadEndDateTime });
    internalState({ isDirty, startTime, totalHours, payloadStartDateTime: startValue, payloadEndDateTime: endValue });
  },
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.EndWrapper.EndDropdown': ({ startDateTimeOriginal, endDateTimeOriginal, selectedOptionId, selectedDate, startTime, payloadStartDateTime, payloadEndDateTime }, _, internalState) => {
    const { isDirty, endTime, totalHours, startValue, endValue } = calculateState({ startTime, endTime: selectedOptionId, selectedDate, startDateTimeOriginal, endDateTimeOriginal, changeEndValue: true, payloadStartDateTime, payloadEndDateTime });
    internalState({ isDirty, endTime, totalHours, payloadStartDateTime: startValue, payloadEndDateTime: endValue });
  },
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.Tech': ({ selectedOptionId }, _, internalState) => {
    internalState({ techId: selectedOptionId });
  },
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.TotalWrapper.TotalHours': async ({ startDateTimeOriginal, endDateTimeOriginal, value, payloadStartDateTime, payloadEndDateTime }, outlet, internalState) => {
    const isValid = isValidTimeFormat(value);
    if (!isValid) {
      await internalState({ isDirty: false, totalHours: value, totalHoursError: 'Type total in hh:mm:ss format' });
      return;
    }

    let time = value.split(':').filter((v) => v.trim());
    let hours = time[0];
    let minutes = time[1];
    let seconds = time[2];
    if (parseInt(seconds) > 59) {
      value = `${hours}:${addZeroPrefix(parseInt(minutes) + 1)}:00`;
    }
    time = value.split(':').filter((v) => v.trim());
    hours = time[0];
    minutes = time[1];
    seconds = time[2];
    if (parseInt(minutes) > 59) {
      value = `${addZeroPrefix(parseInt(hours) + 1)}:00:${seconds}`;
    }

    const start = format(new Date(startDateTimeOriginal), 'hh:mm aa');
    let startValue = createPayLoadDateTime({ time: start, originalDate: payloadStartDateTime });
    const endValue = createPayLoadDateTime({ time: start, originalDate: payloadEndDateTime });
    const { total: totalHours } = calculateHoursBetweenDates({ startDate: startDateTimeOriginal, endDate: endDateTimeOriginal, withSeconds: true });

    const newValue = { totalHours: value };
    const oldValue = { totalHours };
    const isDirty = Object.keys(dirtyValuesFor(oldValue, newValue)).length > 0;

    const newMinutes = getMinutesFromTime(value);
    const oldMinutes = getMinutesFromTime(totalHours);
    const diffMinutes = oldMinutes - newMinutes;

    let newDateTime = new Date(startValue);
    if (diffMinutes < 0) newDateTime = subMinutes(newDateTime, Math.abs(diffMinutes));
    if (diffMinutes > 0) newDateTime = addMinutes(newDateTime, Math.abs(diffMinutes));

    const newSelectedDate = format(newDateTime, 'MM/dd/yy');
    const newStart = format(newDateTime, 'hh:mm aa');

    startValue = createPayLoadDateTime({ time: start, originalDate: new Date(newSelectedDate) });
    internalState({ payloadStartDateTime: startValue, isDirty, startTime: newStart, selectedDate: new Date(newSelectedDate), totalHours: value, totalHoursError: '' });
  },
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.StartWrapper.Calendar': ({ selectedDate, startDateTimeOriginal, endDateTimeOriginal, startTime, endTime, payloadStartDateTime, payloadEndDateTime }, outlet, internalState) => {
    const { isDirty, totalHours, startValue, endValue } = calculateState({ startTime, endTime, selectedDate, startDateTimeOriginal, endDateTimeOriginal, payloadStartDateTime, payloadEndDateTime });
    internalState({ isDirty, startTime, endTime, totalHours, payloadStartDateTime: startValue, payloadEndDateTime: endValue });
  },
  'CreateUpdateTimer.ContainerWrapper.InputsWrapper.EndWrapper.Calendar': ({ selectedDate, startDateTimeOriginal, endDateTimeOriginal, startTime, endTime, payloadStartDateTime, payloadEndDateTime }, outlet, internalState) => {
    const { isDirty, totalHours, endValue, startValue } = calculateState({ startTime, endTime, selectedDate, startDateTimeOriginal, endDateTimeOriginal, changeEndValue: true, payloadStartDateTime, payloadEndDateTime });
    internalState({ isDirty, startTime, endTime, totalHours, payloadStartDateTime: startValue, payloadEndDateTime: endValue });
  },
  'CreateUpdateTimer.ContainerWrapper.Buttons.Submit': ({ action, payload }, outlet, internalState) => {
    const isValid = isBefore(payload.from, payload.to);
    if (!isValid) {
      internalState({ isDirty: false });
      return;
    }
    console.log(payload)
    outlet({ action, payload });
  },
  'CreateUpdateTimer.ContainerWrapper.Buttons.CloseButton': (_, outlet, internalState) => {
    outlet({ action: 'close' });
  }
};
